//
// # EditSymbolPinDialog
//
// Modal dialog for editing an existing symbol pin's properties.
// Fields: Name, Width, Direction, Label Position.
// Pre-populated with the current pin data.
//

import React, { useState, useCallback } from "react";
import { Modal } from "../Modal/Modal";
import { schema } from "GenAlphaSchema";

// Derive direction options from the schema PortDir constant.
const PORT_DIR_OPTIONS = Object.entries(schema.core.PortDir).map(
  ([label, value]) => ({ value, label })
);

import { LABEL_POSITIONS } from "./AddSymbolPinDialog";

const LABEL_POSITION_OPTIONS = Object.entries(LABEL_POSITIONS).map(
  ([label, value]) => ({ value, label })
);

// The result returned when the user confirms the dialog.
export interface EditSymbolPinConfig {
  readonly portName: string;
  readonly width: number;
  readonly dir: schema.core.PortDir;
  readonly labelPosition: string;
}

interface EditSymbolPinDialogProps {
  readonly currentData: {
    readonly portName: string;
    readonly width: number;
    readonly dir: schema.core.PortDir;
    readonly labelPosition: string;
  };
  readonly onConfirm: (config: EditSymbolPinConfig) => void;
  readonly onCancel: () => void;
}

export function EditSymbolPinDialog({
  currentData,
  onConfirm,
  onCancel,
}: EditSymbolPinDialogProps) {
  const [portName, setPortName] = useState(currentData.portName);
  const [dir, setDir] = useState(currentData.dir);
  const [labelPosition, setLabelPosition] = useState(currentData.labelPosition);

  const handleConfirm = useCallback(() => {
    onConfirm({
      portName,
      width: currentData.width,
      dir,
      labelPosition,
    });
  }, [portName, dir, labelPosition, currentData.width, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Edit Symbol Pin">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Port Name */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Name
          </label>
          <input
            type="text"
            value={portName}
            onChange={(e) => setPortName(e.target.value)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={inputStyle}
          />
        </div>

        {/* Bus Width (fixed to 1 for now) */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Bus Width
          </label>
          <input
            type="number"
            value={currentData.width}
            disabled
            className="px-2 py-1.5 text-[13px] rounded border outline-none opacity-50 cursor-not-allowed"
            style={inputStyle}
          />
        </div>

        {/* Direction */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Direction
          </label>
          <select
            value={dir}
            onChange={(e) => setDir(e.target.value as schema.core.PortDir)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {PORT_DIR_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        {/* Label Position */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Label Position
          </label>
          <select
            value={labelPosition}
            onChange={(e) => setLabelPosition(e.target.value)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {LABEL_POSITION_OPTIONS.map((lp) => (
              <option key={lp.value} value={lp.value}>
                {lp.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-ide-accent)",
              color: "#ffffff",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
